import React from 'react';

interface OnboardingProgressBarProps {
  currentStep: string;
  completedSteps: string[];
}

const STEPS = [
  { key: 'welcome', label: 'Welcome', number: 1 },
  { key: 'tos', label: 'Terms', number: 2 },
  { key: 'profile', label: 'Profile', number: 3 },
  { key: 'contact', label: 'Contact', number: 4 },
  { key: 'payment', label: 'Payment', number: 5 },
  { key: 'fair_review', label: 'Review', number: 6 },
  { key: 'done', label: 'Complete', number: 7 },
];

/**
 * Progress bar component for Fair Manager onboarding flow
 * Issue #1455 - Shows 7-step progress with visual indicators
 * WCAG 2.1 AA compliant with ARIA labels and keyboard navigation
 */
const OnboardingProgressBar: React.FC<OnboardingProgressBarProps> = ({
  currentStep,
  completedSteps,
}) => {
  const currentStepIndex = STEPS.findIndex((step) => step.key === currentStep);
  const progressPercentage = ((currentStepIndex + 1) / STEPS.length) * 100;

  const getStepStatus = (stepKey: string): 'completed' | 'current' | 'upcoming' => {
    if (completedSteps.includes(stepKey)) return 'completed';
    if (stepKey === currentStep) return 'current';
    return 'upcoming';
  };

  return (
    <div className="w-full py-6" role="navigation" aria-label="Onboarding progress">
      {/* Progress bar */}
      <div className="relative">
        {/* Background bar */}
        <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200" aria-hidden="true" />

        {/* Progress bar fill */}
        <div
          className="absolute top-4 left-0 h-1 bg-teal-600 transition-all duration-500 ease-out"
          style={{ width: `${progressPercentage}%` }}
          aria-hidden="true"
        />

        {/* Step indicators */}
        <div className="relative flex justify-between">
          {STEPS.map((step) => {
            const status = getStepStatus(step.key);
            const isCompleted = status === 'completed';
            const isCurrent = status === 'current';

            return (
              <div
                key={step.key}
                className="flex flex-col items-center"
                aria-label={`Step ${step.number}: ${step.label}`}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {/* Circle indicator */}
                <div
                  className={`
                    w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold
                    transition-all duration-300 border-2
                    ${
                      isCompleted
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : isCurrent
                        ? 'bg-white border-teal-600 text-teal-600 ring-4 ring-teal-100'
                        : 'bg-white border-gray-300 text-gray-400'
                    }
                  `}
                  role="img"
                  aria-label={
                    isCompleted
                      ? `${step.label} completed`
                      : isCurrent
                      ? `${step.label} in progress`
                      : `${step.label} upcoming`
                  }
                >
                  {isCompleted ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span aria-hidden="true">{step.number}</span>
                  )}
                </div>

                {/* Step label */}
                <div
                  className={`
                    mt-2 text-xs font-medium text-center
                    ${isCurrent ? 'text-teal-700' : isCompleted ? 'text-teal-600' : 'text-gray-500'}
                  `}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Screen reader announcement */}
      <div className="sr-only" role="status" aria-live="polite">
        Step {currentStepIndex + 1} of {STEPS.length}: {STEPS[currentStepIndex]?.label}
      </div>
    </div>
  );
};

export default OnboardingProgressBar;
